// components/OverlayTool.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, RefreshCw, X, Plus, ChevronLeft, ChevronRight,
    Search, ChevronDown
} from 'lucide-react';
import { useCanvasLogic } from '../hooks/useCanvasLogic';
import { useOverlayTool } from '../context/OverlayToolContext';
import ENV from '../config/env';

// Component: Color variant selector for main products
const ProductVariantSelector = ({ product, selectedVariant, onVariantSelect }) => {
    const [showVariantDropdown, setShowVariantDropdown] = useState(false);
    const variantDropdownRef = useRef(null);

    if (!product || product.source !== 'main' || !product.originalProduct?.variants || product.originalProduct.variants.length === 0) {
        return null;
    }

    const variants = product.originalProduct.variants;
    const currentVariant = variants.find(v => v.sku === selectedVariant?.sku);

    const isColorHex = (str) => {
        return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(str);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (variantDropdownRef.current && !variantDropdownRef.current.contains(event.target)) {
                setShowVariantDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="mb-4" ref={variantDropdownRef}>
            <label className="block text-xs text-gray-400 mb-2">Color / Variante:</label>
            <div className="relative">
                <button
                    onClick={() => setShowVariantDropdown(!showVariantDropdown)}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm flex items-center justify-between hover:bg-gray-600 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        {currentVariant && currentVariant.color && isColorHex(currentVariant.color) && (
                            <div
                                className="w-4 h-4 rounded-full border border-gray-500"
                                style={{ backgroundColor: currentVariant.color }}
                            />
                        )}
                        <span>{currentVariant?.color || 'Vista general'}</span>
                    </div>
                    <ChevronDown size={16} className={`transition-transform ${showVariantDropdown ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                    {showVariantDropdown && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                        >
                            <button
                                onClick={() => {
                                    onVariantSelect(null);
                                    setShowVariantDropdown(false);
                                }}
                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 ${!selectedVariant?.sku ? 'bg-primary/20 text-primary' : 'text-gray-300'
                                    }`}
                            >
                                <div className="w-4 h-4 rounded-full border border-gray-500 bg-gray-600" />
                                <span>Vista general</span>
                            </button>

                            {variants.map((variant) => (
                                <button
                                    key={variant.sku}
                                    onClick={() => {
                                        onVariantSelect(variant.sku);
                                        setShowVariantDropdown(false);
                                    }}
                                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 ${selectedVariant?.sku === variant.sku ? 'bg-primary/20 text-primary' : 'text-gray-300'
                                        }`}
                                >
                                    {variant.color && isColorHex(variant.color) ? (
                                        <div
                                            className="w-4 h-4 rounded-full border border-gray-500"
                                            style={{ backgroundColor: variant.color }}
                                        />
                                    ) : (
                                        <div className="w-4 h-4 rounded-full bg-gray-600 border border-gray-500" />
                                    )}
                                    <span>{variant.color || 'Color'}</span>
                                    {variant.images?.length > 0 && (
                                        <span className="text-xs text-gray-400 ml-auto">
                                            {variant.images.length} imágenes
                                        </span>
                                    )}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const OverlayTool = () => {
    const {
        logos,
        selectedLogo,
        selectedProduct,
        selectedProductImage,
        currentProductImages,
        currentImageIndex,
        productOptions,
        productCategories,
        selectedCategory,
        activeSource,
        searchTerm,
        isLoading,
        selectedVariant,
        canvasRef,
        containerRef,
        setSelectedLogo,
        setSelectedProduct,
        setSelectedProductImage,
        setSelectedCategory,
        setActiveSource,
        setSearchTerm,
        setSelectedVariant,
        nextImage,
        prevImage,
        handleFileUpload,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleWheel,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        deleteLogo,
        resetAll,
        captureAndSendQuote,
        injectProduct
    } = useCanvasLogic();

    const { registerInjectHandler } = useOverlayTool();

    // Register injectProduct so ProductDetailModal can trigger it via context
    useEffect(() => {
        registerInjectHandler(injectProduct);
    }, [registerInjectHandler, injectProduct]);

    const [contactForm, setContactForm] = useState({ name: '', phone: '', message: '' });
    const [contactErrors, setContactErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadStatus, setUploadStatus] = useState({ show: false, success: false, message: '' });
    const [debugInfo, setDebugInfo] = useState('');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

    const categoryDropdownRef = useRef(null);
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (selectedProduct) {
            console.log('Selected product:', selectedProduct.name);
            console.log('Product source:', selectedProduct.source);
            console.log('Current product images:', currentProductImages);
            console.log('Current image index:', currentImageIndex);
            console.log('Images count:', currentProductImages?.length);
        }
    }, [selectedProduct, currentProductImages, currentImageIndex]);

    const handleContactSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!contactForm.name.trim()) newErrors.name = 'El nombre es requerido';
        if (!contactForm.phone.trim()) newErrors.phone = 'El teléfono es requerido';
        if (!contactForm.message.trim()) newErrors.message = 'El mensaje es requerido';

        setContactErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setIsSubmitting(true);
        setUploadStatus({ show: true, success: false, message: 'Capturando imagen del diseño...' });
        setDebugInfo('Iniciando proceso...');

        try {
            let quoteImageUrl = '';
            let imageUploaded = false;

            if (logos.length > 0 || selectedProduct) {
                setDebugInfo('Capturando canvas...');
                const captureResult = await captureAndSendQuote(contactForm);
                setDebugInfo(`Resultado: ${JSON.stringify(captureResult)}`);

                if (captureResult.success) {
                    quoteImageUrl = captureResult.imageUrl;
                    imageUploaded = true;
                    setUploadStatus({
                        show: true,
                        success: true,
                        message: '✅ Imagen guardada correctamente'
                    });
                    setDebugInfo(`URL obtenida: ${quoteImageUrl}`);
                } else {
                    setUploadStatus({
                        show: true,
                        success: false,
                        message: `⚠️ No se pudo capturar la imagen: ${captureResult.error}`
                    });
                    setDebugInfo(`Error: ${captureResult.error}`);
                }
            } else {
                setDebugInfo('No hay logos ni producto para capturar');
                setUploadStatus({
                    show: true,
                    success: false,
                    message: '⚠️ No hay diseño para capturar'
                });
            }

            let formattedMessage = `*Nueva Cotización - Print Studio*%0A%0A`;
            formattedMessage += `*Nombre:* ${contactForm.name}%0A`;
            formattedMessage += `*Teléfono:* ${contactForm.phone}%0A`;
            formattedMessage += `*Producto seleccionado:* ${selectedProduct?.name || 'No especificado'}%0A`;
            formattedMessage += `*Tipo:* ${selectedProduct?.source === 'partner' ? 'Promocional' : 'Print Studio'}%0A`;

            if (selectedProduct?.source === 'main' && selectedVariant?.sku) {
                const variant = selectedProduct.originalProduct?.variants?.find(v => v.sku === selectedVariant.sku);
                if (variant) {
                    formattedMessage += `*Variante:* ${variant.color || 'Color'}\n`;
                }
            }

            if (currentProductImages && currentProductImages.length > 1) {
                formattedMessage += `*Imagen:* ${currentImageIndex + 1} de ${currentProductImages.length}%0A`;
            }

            formattedMessage += `*Logos cargados:* ${logos.length}%0A`;

            if (logos.length > 0) {
                formattedMessage += `*Dimensiones logos:*%0A`;
                logos.forEach((logo, idx) => {
                    formattedMessage += `  Logo ${idx + 1}: ${Math.round(logo.width)}x${Math.round(logo.height)}px, ${Math.round(logo.rotation)}°%0A`;
                });
            }

            formattedMessage += `*Mensaje:*%0A${contactForm.message}%0A%0A`;

            if (imageUploaded && quoteImageUrl) {
                const fullUrl = ENV.FILE_SERVER_URL + quoteImageUrl;
                formattedMessage += `*🔗 VISTA PREVIA DEL DISEÑO:*%0A`;
                formattedMessage += `${fullUrl}%0A`;
                formattedMessage += `_El enlace expira en 24 horas_%0A%0A`;
                setDebugInfo(`Link generado: ${fullUrl}`);
            } else {
                formattedMessage += `_Nota: No se pudo generar la imagen del diseño_%0A%0A`;
            }

            formattedMessage += `_Enviado desde el Visualizador de Logos_`;

            const whatsappUrl = `https://wa.me/+525665650290?text=${formattedMessage}`;
            window.open(whatsappUrl, '_blank');

            setTimeout(() => {
                setUploadStatus({ show: false, success: false, message: '' });
                setDebugInfo('');
            }, 8000);

            setContactForm({ name: '', phone: '', message: '' });

        } catch (error) {
            console.error('Error sending quote:', error);
            setUploadStatus({
                show: true,
                success: false,
                message: `❌ Error: ${error.message}`
            });
            setDebugInfo(`Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        setSelectedVariant(null);
    };

    const mainCount = productOptions.filter(p => p?.source === 'main').length;
    const partnerCount = productOptions.filter(p => p?.source === 'partner').length;

    return (
        <section id="logotool" className="min-h-screen bg-black py-8 md:py-20 relative">
            <div className="absolute inset-0">
                {[...Array(50)].map((_, s) => (
                    <motion.div
                        key={s}
                        className="absolute w-1 h-1 bg-secondary rounded-full"
                        style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
                        animate={{ opacity: [0.2, 1, 0.2], scale: [1, 1.5, 1] }}
                        transition={{ duration: Math.random() * 3 + 2, repeat: Infinity, delay: Math.random() * 2 }}
                    />
                ))}
            </div>

            <div className="absolute inset-0 overflow-hidden">
                {[...Array(15)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute rounded-full bg-gradient-to-r from-primary/10 to-secondary/10"
                        style={{
                            width: Math.random() * 200 + 50,
                            height: Math.random() * 200 + 50,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{ y: [0, -50, 0], x: [0, 30, 0], rotate: [0, 180, 360] }}
                        transition={{ duration: Math.random() * 15 + 10, repeat: Infinity, ease: "linear" }}
                    />
                ))}
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-8 md:mb-12"
                >
                    <h2 className="text-4xl md:text-7xl font-bold gradient-text mb-8 md:mb-8 drop-shadow-xl">
                        Visualizador de Logos
                    </h2>
                    <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
                        ¡Sube tu logo y visualízalo en nuestros productos!
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 mb-8 md:mt-12 glass-effect rounded-xl md:rounded-2xl p-4 md:p-6"
                >
                    <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4 flex items-center space-x-2">
                        <span>💡</span>
                        <span>Cómo usar el Visualizador?</span>
                    </h3>
                    <div className="grid md:grid-cols-4 gap-4 md:gap-6 text-gray-300">
                        <div>
                            <h4 className="font-semibold text-primary mb-1 md:mb-2 text-sm md:text-base">1. Selecciona un producto</h4>
                            <ul className="space-y-1 text-xs md:text-sm">
                                <li>• Filtra por categoría usando el menú desplegable</li>
                                <li>• Elige entre Print Studio y Promocionales</li>
                                <li>• Usa el buscador para encontrar productos específicos</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-primary mb-1 md:mb-2 text-sm md:text-base">2. Selecciona tu logo</h4>
                            <ul className="space-y-1 text-xs md:text-sm">
                                <li>• Elige archivos PNG, JPG, JPEG</li>
                                <li>• <strong>Máximo 2 logos permitidos</strong></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-primary mb-1 md:mb-2 text-sm md:text-base">3. Visualiza tu logo</h4>
                            <ul className="space-y-1 text-xs md:text-sm">
                                <li>• <strong>Haz click/toca</strong> en el canvas para seleccionarlo</li>
                                <li>• <strong>Arrastra el logo</strong> para moverlo</li>
                                <li>• <strong>Arrastra el círculo amarillo</strong> para rotar</li>
                                <li>• <strong>Arrastra las esquinas azules</strong> para cambiar tamaño</li>
                                {isMobile && (
                                    <li>• <strong>Usa dos dedos para hacer zoom</strong> en el logo seleccionado</li>
                                )}
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-green-600 font-bold mb-1 md:mb-2 text-sm md:text-base">4. Cotiza</h4>
                            <ul className="space-y-1 text-xs md:text-sm">
                                <li>• Completa el formulario y envía tu diseño</li>
                                <li>• Recibiremos la muestra de tu logo en el producto</li>
                                <li>• Te contactaremos vía WhatsApp</li>
                            </ul>
                        </div>
                    </div>
                </motion.div>

                <div className="flex flex-col-reverse lg:grid lg:grid-cols-3 gap-6 md:gap-8">
                    <div className="lg:col-span-1 space-y-4 md:space-y-6 order-2 lg:order-1">
                        <div className="glass-effect rounded-xl md:rounded-2xl p-4 md:p-6">
                            <h3 className="text-lg md:text-xl font-bold text-white mb-4">🎯 1. Selecciona un producto</h3>

                            <div className="mb-4">
                                <label className="block text-xs text-gray-400 mb-2">Tipo de producto:</label>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        onClick={() => setActiveSource('all')}
                                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${activeSource === 'all'
                                            ? 'bg-primary text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            }`}
                                    >
                                        Todos ({mainCount + partnerCount})
                                    </button>
                                    <button
                                        onClick={() => setActiveSource('main')}
                                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${activeSource === 'main'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            }`}
                                    >
                                        Print Studio ({mainCount})
                                    </button>
                                    <button
                                        onClick={() => setActiveSource('partner')}
                                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${activeSource === 'partner'
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            }`}
                                    >
                                        Promocionales ({partnerCount})
                                    </button>
                                </div>
                            </div>

                            {productCategories && productCategories.length > 0 ? (
                                <div className="mb-4" ref={categoryDropdownRef}>
                                    <label className="block text-xs text-gray-400 mb-2">Categoría:</label>
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                                            className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm flex items-center justify-between hover:bg-gray-600 transition-colors"
                                        >
                                            <span className="truncate">{selectedCategory || 'Todas las categorías'}</span>
                                            <ChevronDown size={16} className={`transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                                        </button>

                                        <AnimatePresence>
                                            {showCategoryDropdown && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                                                >
                                                    <button
                                                        onClick={() => {
                                                            setSelectedCategory('');
                                                            setShowCategoryDropdown(false);
                                                        }}
                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700 transition-colors ${!selectedCategory ? 'bg-primary/20 text-primary font-medium' : 'text-gray-300'
                                                            }`}
                                                    >
                                                        Todas las categorías
                                                    </button>
                                                    {productCategories.map((category) => (
                                                        <button
                                                            key={category}
                                                            onClick={() => {
                                                                setSelectedCategory(category);
                                                                setShowCategoryDropdown(false);
                                                            }}
                                                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700 transition-colors ${selectedCategory === category
                                                                ? 'bg-primary/20 text-primary'
                                                                : 'text-gray-300'
                                                                }`}
                                                        >
                                                            {category}
                                                        </button>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-4 text-gray-400 text-xs">No hay categorías disponibles</div>
                            )}

                            {selectedProduct && selectedProduct.source === 'main' && (
                                <ProductVariantSelector
                                    product={selectedProduct}
                                    selectedVariant={selectedVariant}
                                    onVariantSelect={setSelectedVariant}
                                />
                            )}

                            <div className="mb-4">
                                <label className="block text-xs text-gray-400 mb-2">Buscar producto:</label>
                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Nombre o marca..."
                                        className="w-full pl-9 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <div className="mb-3 text-xs text-gray-400">
                                {productOptions.length} productos encontrados
                            </div>

                            {isLoading ? (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                                    <p className="text-gray-400 mt-2">Cargando productos...</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2 md:gap-3 max-h-[350px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-600">
                                    {productOptions.map((product) => (
                                        <button
                                            key={product.id}
                                            onClick={() => handleProductSelect(product)}
                                            className={`p-3 rounded-xl smooth-transition flex flex-col items-center justify-center text-center relative group ${selectedProduct?.id === product.id
                                                ? 'bg-primary text-white ring-2 ring-primary ring-offset-2 ring-offset-gray-900'
                                                : product.source === 'partner'
                                                    ? 'bg-purple-900/30 text-gray-300 hover:bg-purple-900/50 border border-purple-500/20'
                                                    : 'bg-secondary/20 text-gray-300 hover:bg-secondary/40 border border-secondary/30'
                                                }`}
                                            style={{ minHeight: '70px' }}
                                        >
                                            <span className="text-xs font-medium line-clamp-2 break-words w-full">
                                                {product.name}
                                            </span>

                                            {product.images?.length > 1 && (
                                                <span className="text-[9px] mt-1.5 opacity-60 flex items-center justify-center gap-1">
                                                    <span>{product.images.length} imágenes</span>
                                                </span>
                                            )}

                                            {product.images?.length === 1 && (
                                                <span className="text-[9px] mt-1.5 opacity-60 flex items-center justify-center gap-1">
                                                    <span>1 imagen</span>
                                                </span>
                                            )}
                                        </button>
                                    ))}

                                    {productOptions.length === 0 && (
                                        <div className="col-span-2 text-center py-8 text-gray-400 text-sm">
                                            No se encontraron productos
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="glass-effect rounded-xl md:rounded-2xl p-4 md:p-6">
                            <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">📤 2. Selecciona tu logo</h3>
                            <div className="border-2 border-dashed border-gray-600 rounded-xl md:rounded-2xl p-4 md:p-8 text-center">
                                <Upload size={isMobile ? 32 : 48} className="mx-auto text-gray-400 mb-3 md:mb-4" />
                                <p className="text-sm md:text-base text-gray-300 mb-3 md:mb-4">
                                    Sólo archivos PNG, JPG o JPEG.
                                </p>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/png, image/jpeg, image/jpg"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="logo-upload"
                                    disabled={logos.length >= 2}
                                />
                                <label
                                    htmlFor="logo-upload"
                                    className={`px-4 md:px-6 py-2 md:py-3 rounded-full cursor-pointer smooth-transition inline-flex items-center space-x-2 ${logos.length >= 2
                                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                        : 'bg-primary hover:bg-primary/90 text-white'
                                        }`}
                                >
                                    <Plus size={16} className="md:hidden" />
                                    <Plus size={20} className="hidden md:block" />
                                    <span className="text-sm md:text-base">
                                        {isMobile ? `Logos (${logos.length}/2)` : `Seleccionar Logos (${logos.length}/2)`}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2 order-1 lg:order-2">
                        <div className="glass-effect rounded-xl md:rounded-2xl p-4 md:p-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 space-y-2 md:space-y-0">
                                <h3 className="text-lg md:text-xl font-bold text-white">🎨 3. Visualiza tu logo</h3>
                                <div className="flex space-x-2 md:space-x-3">
                                    <button
                                        onClick={resetAll}
                                        className="px-3 md:px-4 py-1.5 md:py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 smooth-transition flex items-center space-x-1 md:space-x-2 text-sm md:text-base"
                                    >
                                        <RefreshCw size={16} className="md:hidden" />
                                        <RefreshCw size={20} className="hidden md:block" />
                                        <span>Reiniciar</span>
                                    </button>
                                </div>
                            </div>

                            <div
                                ref={containerRef}
                                className="relative bg-gray-900 rounded-xl overflow-hidden border-2 border-gray-700"
                                style={{
                                    minHeight: isMobile ? '300px' : '500px',
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                }}
                            >
                                <canvas
                                    ref={canvasRef}
                                    className="w-full h-full cursor-move"
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    onWheel={handleWheel}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    onContextMenu={(e) => e.preventDefault()}
                                    style={{
                                        touchAction: 'none',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                        display: 'block'
                                    }}
                                />

                                {currentProductImages && currentProductImages.length > 1 && (
                                    <>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                prevImage();
                                            }}
                                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-primary text-white p-2 rounded-full transition-colors z-10"
                                            aria-label="Imagen anterior"
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                nextImage();
                                            }}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-primary text-white p-2 rounded-full transition-colors z-10"
                                            aria-label="Siguiente imagen"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full z-10">
                                            {currentImageIndex + 1} / {currentProductImages.length}
                                        </div>
                                    </>
                                )}

                                {/*  small indicator at the bottom right if there are multiple images */}
                                {currentProductImages && currentProductImages.length > 1 && (
                                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full z-10">
                                        {currentProductImages.length} imágenes
                                    </div>
                                )}

                                {selectedProduct && (
                                    <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1 z-10">
                                        <span className="text-white text-xs flex items-center gap-1">
                                            <span className={selectedProduct.source === 'partner' ? 'text-purple-400' : 'text-blue-400'}>
                                                {selectedProduct.source === 'partner' ? 'Promocional' : 'Print Studio'}
                                            </span>
                                            <span className="text-gray-400">|</span>
                                            <span className="text-gray-300 truncate max-w-[150px]">{selectedProduct.name}</span>
                                            {selectedProduct.source === 'main' && selectedVariant?.sku && (
                                                <>
                                                    <span className="text-gray-400">|</span>
                                                    <span className="text-primary text-xs">
                                                        {selectedProduct.originalProduct?.variants?.find(v => v.sku === selectedVariant.sku)?.color || 'Variante'}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {!isMobile && logos.length > 0 && (
                                    <div className="absolute bottom-4 left-4 pointer-events-none">
                                        <div className="bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2">
                                            <span className="text-white text-sm">
                                                <span className="text-primary font-semibold">
                                                    {selectedLogo !== null ? (
                                                        <>
                                                            <span className="block">🖱️ Click + arrastra = Mover</span>
                                                            <span className="block">🟡 Arrastra círculo amarillo = Rotar</span>
                                                            <span className="block">🔵 Arrastra esquinas azules = Escalar</span>
                                                        </>
                                                    ) : (
                                                        'Click en cualquier logo para seleccionarlo'
                                                    )}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {isMobile && logos.length > 0 && (
                                    <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                                        <div className="inline-block bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1.5 mx-2">
                                            <span className="text-white text-xs">
                                                <span className="text-primary font-semibold">
                                                    {selectedLogo !== null ? (
                                                        'Arrastra para mover, toca amarillo para rotar'
                                                    ) : (
                                                        'Toca cualquier logo para seleccionarlo'
                                                    )}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {selectedLogo !== null && (
                                    <div className="absolute top-12 left-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1 pointer-events-none z-10">
                                        <span className="text-white text-xs">
                                            Logo seleccionado: <span className="text-primary font-semibold">#{selectedLogo + 1}</span>
                                        </span>
                                    </div>
                                )}
                            </div>

                            {logos.length > 0 && (
                                <div className="mt-4 md:mt-6 bg-gray-800/50 rounded-xl p-3 md:p-4">
                                    <div className="flex items-center justify-between mb-2 md:mb-3">
                                        <h4 className="text-white font-semibold text-base md:text-lg">📁 Logos Subidos</h4>
                                        <div className="text-xs text-gray-400">{logos.length}/2 logos</div>
                                    </div>
                                    <div className="space-y-2">
                                        {logos.map((logo, index) => (
                                            <div
                                                key={logo.id}
                                                className={`flex items-center justify-between p-2 md:p-3 rounded-lg smooth-transition cursor-pointer group ${selectedLogo === index
                                                    ? 'bg-primary/20 border border-primary/30'
                                                    : 'bg-gray-700/50 hover:bg-gray-700'
                                                    }`}
                                                onClick={() => setSelectedLogo(index)}
                                            >
                                                <div className="flex items-center space-x-2 md:space-x-3">
                                                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded flex items-center justify-center ${selectedLogo === index ? 'bg-primary/30' : 'bg-gray-600'
                                                        }`}>
                                                        <span className="text-white text-sm md:text-base">
                                                            {index === 0 ? '🅰️' : '🅱️'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-2">
                                                            <p className="text-white text-sm font-medium">Logo {index + 1}</p>
                                                            {selectedLogo === index && (
                                                                <span className="text-xs px-1.5 py-0.5 bg-primary/30 text-primary rounded">
                                                                    Seleccionado
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-gray-400 text-xs">
                                                            {Math.round(logo.width)}×{Math.round(logo.height)}px • {Math.round(logo.rotation)}°
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-1 md:space-x-2">
                                                    {selectedLogo === index && (
                                                        <div className="w-2 h-2 md:w-3 md:h-3 bg-primary rounded-full animate-pulse" />
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedLogo(index);
                                                            deleteLogo();
                                                        }}
                                                        className="text-red-400 hover:text-red-300 p-1 opacity-0 group-hover:opacity-100 smooth-transition"
                                                        title="Eliminar logo"
                                                    >
                                                        <X size={14} className="md:hidden" />
                                                        <X size={16} className="hidden md:block" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 mb-8 md:mt-12 glass-effect rounded-xl md:rounded-2xl p-4 md:p-6"
                >
                    <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4 flex items-center space-x-2">
                        <span>✅</span>
                        <span>¿Te gusta el resultado?, ¡cotiza ya mismo!</span>
                    </h3>
                    <p className="text-gray-300 text-sm mt-2 mb-8">
                        Completa el formulario y recibiremos tu propuesta para cotizar por WhatsApp.
                    </p>

                    {uploadStatus.show && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`mb-4 p-3 rounded-lg ${uploadStatus.success ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                                }`}
                        >
                            {uploadStatus.message}
                        </motion.div>
                    )}

                    {debugInfo && process.env.NODE_ENV !== 'production' && (
                        <div className="mb-4 p-3 bg-gray-800 rounded-lg text-xs text-gray-400 font-mono overflow-auto max-h-32">
                            <pre>{debugInfo}</pre>
                        </div>
                    )}

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="bg-gray-800/70 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl w-full overflow-hidden"
                    >
                        <form className="p-4 md:p-6 space-y-4" onSubmit={handleContactSubmit}>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="text-primary">👤</span> Nombre completo *
                                </label>
                                <input
                                    type="text"
                                    value={contactForm.name}
                                    onChange={(e) => {
                                        setContactForm({ ...contactForm, name: e.target.value });
                                        if (contactErrors.name) setContactErrors({ ...contactErrors, name: '' });
                                    }}
                                    className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border ${contactErrors.name ? 'border-red-500' : 'border-gray-700'
                                        } text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                    placeholder="Ingresa tu nombre"
                                />
                                {contactErrors.name && <p className="text-red-400 text-xs mt-1">{contactErrors.name}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="text-primary">📱</span> Teléfono *
                                </label>
                                <input
                                    type="tel"
                                    value={contactForm.phone}
                                    onChange={(e) => {
                                        setContactForm({ ...contactForm, phone: e.target.value });
                                        if (contactErrors.phone) setContactErrors({ ...contactErrors, phone: '' });
                                    }}
                                    className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border ${contactErrors.phone ? 'border-red-500' : 'border-gray-700'
                                        } text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                    placeholder="Ej: +52 55 1234 5678"
                                />
                                {contactErrors.phone && <p className="text-red-400 text-xs mt-1">{contactErrors.phone}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="text-primary">💭</span> Mensaje *
                                </label>
                                <textarea
                                    rows="4"
                                    value={contactForm.message}
                                    onChange={(e) => {
                                        setContactForm({ ...contactForm, message: e.target.value });
                                        if (contactErrors.message) setContactErrors({ ...contactErrors, message: '' });
                                    }}
                                    className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border ${contactErrors.message ? 'border-red-500' : 'border-gray-700'
                                        } text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none`}
                                    placeholder="Describe tu proyecto, especificaciones, colores, etc..."
                                />
                                {contactErrors.message && <p className="text-red-400 text-xs mt-1">{contactErrors.message}</p>}
                            </div>

                            <div className="bg-gray-900/30 rounded-lg p-3 border border-gray-700/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-yellow-400">🖼️</span>
                                    <span className="text-sm font-medium text-gray-300">Información del diseño</span>
                                </div>
                                <div className="text-xs text-gray-400 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Producto seleccionado:</span>
                                        <span className="text-white">{selectedProduct?.name || 'Ninguno'}</span>
                                    </div>
                                    {selectedProduct && (
                                        <div className="flex justify-between">
                                            <span>Tipo:</span>
                                            <span className={selectedProduct.source === 'partner' ? 'text-purple-400' : 'text-blue-400'}>
                                                {selectedProduct.source === 'partner' ? 'Promocional' : 'Print Studio'}
                                            </span>
                                        </div>
                                    )}
                                    {selectedProduct?.source === 'main' && selectedVariant?.sku && (
                                        <div className="flex justify-between">
                                            <span>Variante:</span>
                                            <span className="text-white">
                                                {selectedProduct.originalProduct?.variants?.find(v => v.sku === selectedVariant.sku)?.color || 'Color'}
                                            </span>
                                        </div>
                                    )}
                                    {currentProductImages && currentProductImages.length > 1 && (
                                        <div className="flex justify-between">
                                            <span>Imagen:</span>
                                            <span className="text-white">Imagen {currentImageIndex + 1} de {currentProductImages.length}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span>Logos cargados:</span>
                                        <span className="text-white">{logos.length}</span>
                                    </div>
                                    {logos.length > 0 && (
                                        <div className="border-t border-gray-700/50 mt-2 pt-2">
                                            <span className="text-green-400">✓ Se incluirá captura del diseño</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <motion.button
                                type="submit"
                                disabled={isSubmitting}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`w-full py-4 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${isSubmitting
                                    ? 'bg-gray-600 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                                    } text-white`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.966-.944 1.162-.175.195-.349.22-.649.075-.3-.15-1.263-.465-2.403-1.485-.888-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.3-.345.45-.52.15-.174.2-.298.3-.497.1-.198.05-.371-.025-.52-.075-.148-.675-1.62-.922-2.206-.24-.584-.487-.504-.675-.51-.174-.007-.371-.011-.568-.011-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.148.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.767-.721 2.016-1.426.255-.705.255-1.31.178-1.426-.078-.115-.279-.174-.58-.324z" />
                                            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.66 4.08 1.757 5.699L.03 23.58c-.094.338.03.69.306.922.275.23.675.3 1.002.155l5.494-2.33A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                                        </svg>
                                        Enviar por WhatsApp
                                    </>
                                )}
                            </motion.button>

                            <p className="text-gray-400 text-xs text-center">
                                Al enviar este formulario, aceptas que nos contactemos contigo por WhatsApp.
                                <span className="block text-green-400 mt-1">
                                    {logos.length > 0
                                        ? '✓ Se incluirá un link con la imagen de tu diseño (válido 24h)'
                                        : '✓ Sube un logo para incluir imagen en la cotización'}
                                </span>
                            </p>
                        </form>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default OverlayTool;